import React, {useCallback} from "react";

import cn from "classnames";
import classes from "./sortbutton.module.css";
import {SortDirection} from "../schema";
import {NoSortIcon, SortIcon} from "./icons";

export type SortInput<TField> = {
  field?: TField | null;
  direction?: SortDirection | null;
};

type SortButtonProps<TField, TSortInput extends SortInput<TField>> = {
  onSetSort: React.Dispatch<React.SetStateAction<TSortInput>>;
  field: TField;
  selectedSort: TSortInput;
  defaultSort: TSortInput;
} & React.ComponentProps<"button">;

const SortButton = function SortButton<TField, TSortInput extends SortInput<TField>>({
  className: extraClassName,
  disabled,
  onSetSort: setSort,
  selectedSort,
  field,
  defaultSort,
  ...props
}: SortButtonProps<TField, TSortInput>) {
  const handleChangeSort = useCallback<(sort: {field?: TField;
     direction?: SortDirection}) => void>(
    ({field, direction}) => {
      if (field === undefined) {
        setSort(defaultSort);
        return;
      }

      if (direction) {
        setSort({field, direction} as TSortInput);
        return;
      }

      switch (selectedSort.direction) {
        case SortDirection.ASC: {
          setSort({field, direction: SortDirection.DESC} as TSortInput);
          return;
        }
        case SortDirection.DESC: {
          setSort(defaultSort);
          return;
        }
        default: {
          setSort({field, direction: SortDirection.ASC} as TSortInput);
          return;
        }
      }
    },
    [setSort, defaultSort, selectedSort.direction],
  );

  return (
    <div className={classes.root}>
      {selectedSort.field === field ? (
        <button
          type="button"
          className={cn(
            selectedSort.direction === SortDirection.ASC ? classes.rotate : "",
            classes.active,
            extraClassName,
          )}
          onClick={() => handleChangeSort({field})}
          {...props}
        >
          <SortIcon />
        </button>
      ) : defaultSort.field !== field ? (
        <button
          type="button"
          className={cn(classes.notSelect, extraClassName)}
          onClick={() => handleChangeSort({field, direction: SortDirection.ASC})}
          {...props}
        >
          <NoSortIcon />
        </button>
      ) : (
        <button
          type="button"
          className={cn(
            defaultSort.direction === SortDirection.ASC ? classes.rotate : "",
            classes.default,
            extraClassName,
          )}
          onClick={() => handleChangeSort({})}
          {...props}
        >
          <SortIcon />
        </button>
      )}
    </div>
  );
};

export default SortButton;
